import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MuiAvatar from "@mui/material/Avatar";
import Link from "@mui/material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StarIcon from "@mui/icons-material/Star";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { cleanList, cleanText, hasDoctorContactInfo, } from "./utils/doctorDisplay";
import { getDoctorConsultationFee, } from "./utils/workspaceDoctors";
import { getCurrencySymbol } from "../../constants";
import { DoctorContactSection } from "./DoctorContactSection";
import { useTranslation } from "../../hooks";
import { sanitizeRichHtml } from "../../utils/richText";
function getInitials(name) {
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
}
const AVATAR_COLORS = [
    { bg: "#dbeafe", color: "#2563eb" },
    { bg: "#dcfce7", color: "#16a34a" },
    { bg: "#fce7f3", color: "#db2777" },
    { bg: "#e0e7ff", color: "#4f46e5" },
    { bg: "#ffedd5", color: "#ea580c" },
];
function getColorIndex(name) {
    let hash = 0;
    for (let i = 0; i < name.length; i++)
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return Math.abs(hash) % AVATAR_COLORS.length;
}
function getQualificationSecondaryText(qualification) {
    if (!qualification)
        return "";
    const institution = "institution" in qualification ? qualification.institution : undefined;
    const college = "college" in qualification ? qualification.college : undefined;
    return [qualification.degree, institution || college]
        .filter(Boolean)
        .join(" \u2014 ");
}
function getIdentityData(doctor, display, consultationMode, visitorFeeAudience) {
    const prefix = cleanText(display.prefix);
    const name = cleanText(display.name);
    const displayName = prefix && name ? `${prefix} ${name}` : (name ?? "");
    const avatarSeed = displayName;
    const fee = consultationMode && visitorFeeAudience
        ? getDoctorConsultationFee(doctor, consultationMode, visitorFeeAudience)
        : null;
    return {
        displayName,
        specializationText: cleanList(display.specializations).join(" & "),
        experienceText: cleanText(display.experience),
        initials: getInitials(avatarSeed),
        avatarStyle: AVATAR_COLORS[getColorIndex(avatarSeed)],
        profileImageUrl: display.profileImageUrl ?? undefined,
        feeText: fee
            ? `${getCurrencySymbol(fee.currency)}${fee.amount.toLocaleString()}`
            : null,
    };
}
export function DoctorCard({ doctor, city, isSelected, onSelect, solo, consultationMode, feeAudience, display, variant = "panel", }) {
    if (solo) {
        return (_jsx(DoctorSoloProfile, { doctor: doctor, display: display, city: city, consultationMode: consultationMode, feeAudience: feeAudience, variant: variant }));
    }
    return (_jsx(DoctorListRow, { doctor: doctor, display: display, consultationMode: consultationMode, feeAudience: feeAudience, isSelected: isSelected, onSelect: onSelect }));
}
const DoctorSoloProfile = React.memo(function DoctorSoloProfile({ doctor, display, city, consultationMode, feeAudience, variant, }) {
    const { t } = useTranslation("appointment");
    const identity = getIdentityData(doctor, display, consultationMode, feeAudience);
    const sections = React.useMemo(() => buildProfileSections(display, t), [display, t]);
    const isHeader = variant === "header";
    const content = {
        avatar: (_jsx(DoctorAvatar, { displayName: identity.displayName, photoUrl: identity.profileImageUrl, initials: identity.initials, avatarStyle: identity.avatarStyle, size: isHeader ? 56 : 160, fontSize: isHeader ? 20 : 48 })),
        displayName: identity.displayName,
        specializationText: identity.specializationText,
        designation: cleanText(display.designation),
        rating: display.googleRating,
        city,
        feeText: identity.feeText,
        experienceText: identity.experienceText,
        registrationNumber: display.registrationNumber,
        registrationAuthority: display.registrationAuthority,
        sections,
        t,
    };
    return isHeader ? (_jsx(DoctorProfileHeaderBand, { ...content })) : (_jsx(DoctorProfilePanel, { ...content }));
});
function DoctorIdentity({ avatar, displayName, specializationText, designation, inline = false, }) {
    const text = (_jsxs(Box, { sx: { minWidth: 0, textAlign: inline ? "left" : "center" }, children: [displayName && (_jsx(Typography, { sx: {
                    fontWeight: 700,
                    lineHeight: 1.25,
                    fontSize: inline ? 17 : 22,
                    mt: inline ? 0 : 2,
                }, children: displayName })), specializationText && (_jsx(Typography, { sx: {
                    color: "primary.main",
                    fontWeight: 500,
                    mt: 0.5,
                    lineHeight: 1.4,
                    fontSize: inline ? 13.5 : 15,
                }, children: specializationText })), designation && (_jsx(Typography, { variant: "caption", color: "text.secondary", sx: { display: "block", mt: 0.25, lineHeight: 1.4 }, children: designation }))] }));
    if (!inline) {
        return (_jsxs(Stack, { alignItems: "center", sx: { textAlign: "center" }, children: [avatar, text] }));
    }
    return (_jsxs(Stack, { direction: "row", spacing: 1.75, alignItems: "center", sx: { minWidth: 0 }, children: [_jsx(Box, { sx: { flexShrink: 0 }, children: avatar }), text] }));
}
function DoctorStatChips({ rating, city, feeText, align = "center", }) {
    const chips = [];
    if (rating != null) {
        chips.push(_jsx(StatChip, { icon: _jsx(StarIcon, { sx: { fontSize: 16, color: "#cba34c" } }), value: rating.toFixed(1) }, "rating"));
    }
    if (city) {
        chips.push(_jsx(StatChip, { icon: _jsx(LocationOnIcon, { sx: { fontSize: 16, color: "#27903f" } }), value: city }, "city"));
    }
    if (feeText) {
        chips.push(_jsx(StatChip, { value: feeText }, "fee"));
    }
    if (chips.length === 0)
        return null;
    return (_jsx(Stack, { direction: "row", flexWrap: "wrap", justifyContent: align, gap: 0.75, children: chips }));
}
function StatChip({ icon, value, }) {
    return (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 0.75, sx: {
            borderRadius: 1.25,
            bgcolor: "rgba(203, 163, 76, 0.12)",
            color: "text.primary",
            px: 1.5,
            py: 0.75,
            minWidth: 76,
            justifyContent: "center",
        }, children: [icon, _jsx(Typography, { sx: { fontWeight: 600, lineHeight: 1, fontSize: 13 }, children: value })] }));
}
function DoctorHighlights({ experienceText, inline = false, t, }) {
    const items = (_jsxs(_Fragment, { children: [_jsx(FeatureItem, { icon: _jsx(CheckCircleIcon, { sx: { fontSize: inline ? 16 : 20, color: "success.main" } }), text: t("verifiedDoctor", "Verified Doctor"), dense: inline }), experienceText && (_jsx(FeatureItem, { icon: _jsx(SchoolIcon, { sx: { fontSize: inline ? 16 : 20, color: "primary.main" } }), text: t("yearExperience", undefined, { count: experienceText }), dense: inline })), _jsx(FeatureItem, { icon: _jsx(EmojiEventsIcon, { sx: { fontSize: inline ? 16 : 20, color: "success.main" } }), text: t("patientsTreated", "1000+ Patients Treated"), dense: inline })] }));
    if (inline) {
        return (_jsx(Stack, { direction: "row", flexWrap: "wrap", rowGap: 0.75, columnGap: 2, children: items }));
    }
    return (_jsx(Box, { sx: {
            border: 1,
            borderColor: "divider",
            borderRadius: 1.5,
            px: 2,
            py: 1.75,
        }, children: _jsx(Stack, { spacing: 1.25, children: items }) }));
}
function RegistrationNote({ registrationNumber, registrationAuthority, t, }) {
    if (!cleanText(registrationNumber))
        return null;
    return (_jsxs(Typography, { variant: "caption", color: "text.disabled", sx: { display: "block", lineHeight: 1.4 }, children: [t("registrationNumber", undefined, { number: registrationNumber }), registrationAuthority
                ? t("registrationAuthority", undefined, {
                    authority: registrationAuthority,
                })
                : ""] }));
}
function DoctorProfilePanel({ avatar, displayName, specializationText, designation, rating, city, feeText, experienceText, registrationNumber, registrationAuthority, sections, t, }) {
    return (_jsx(Box, { sx: { flex: 1, minHeight: 0, overflowY: "auto" }, children: _jsxs(Stack, { spacing: 2, sx: { p: "24px 20px 22px" }, children: [_jsx(DoctorIdentity, { avatar: avatar, displayName: displayName, specializationText: specializationText, designation: designation }), _jsx(DoctorStatChips, { rating: rating, city: city, feeText: feeText }), _jsx(DoctorHighlights, { experienceText: experienceText, t: t }), _jsx(RegistrationNote, { registrationNumber: registrationNumber, registrationAuthority: registrationAuthority, t: t }), sections.length > 0 && _jsx(ProfileSectionList, { sections: sections })] }) }));
}
function DoctorProfileHeaderBand({ avatar, displayName, specializationText, designation, rating, city, feeText, experienceText, registrationNumber, registrationAuthority, sections, t, }) {
    const [expanded, setExpanded] = React.useState(false);
    const hasSections = sections.length > 0;
    return (_jsxs(Box, { sx: { flexShrink: 0, borderBottom: 1, borderColor: "divider" }, children: [_jsxs(Box, { sx: { p: "14px 16px 12px" }, children: [_jsxs(Stack, { direction: "row", alignItems: "flex-start", justifyContent: "space-between", spacing: 1.5, children: [_jsx(DoctorIdentity, { inline: true, avatar: avatar, displayName: displayName, specializationText: specializationText, designation: designation }), hasSections && (_jsxs(Box, { component: "button", type: "button", onClick: () => setExpanded((v) => !v), "aria-expanded": expanded, sx: {
                                    flexShrink: 0,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                    px: 1,
                                    py: 0.5,
                                    mt: 0.25,
                                    border: 0,
                                    bgcolor: "transparent",
                                    borderRadius: 1,
                                    color: "primary.main",
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    fontFamily: "inherit",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    "&:hover": { bgcolor: "action.hover" },
                                    "&:focus-visible": {
                                        outline: "2px solid",
                                        outlineColor: "primary.main",
                                        outlineOffset: 1,
                                    },
                                }, children: [expanded ? t("showLess", "Show less") : t("viewAll", "View all"), _jsx(ExpandMoreIcon, { sx: {
                                            fontSize: 18,
                                            transition: "transform 0.2s",
                                            transform: expanded ? "rotate(180deg)" : "none",
                                        } })] }))] }), _jsxs(Stack, { spacing: 0.75, sx: { mt: 1.25 }, children: [_jsxs(Stack, { direction: "row", flexWrap: "wrap", alignItems: "center", rowGap: 1, columnGap: 2, children: [_jsx(DoctorStatChips, { rating: rating, city: city, feeText: feeText, align: "flex-start" }), _jsx(DoctorHighlights, { inline: true, experienceText: experienceText, t: t })] }), _jsx(RegistrationNote, { registrationNumber: registrationNumber, registrationAuthority: registrationAuthority, t: t })] })] }), hasSections && expanded && (_jsx(Box, { sx: {
                    maxHeight: 280,
                    overflowY: "auto",
                    borderTop: 1,
                    borderColor: "divider",
                    p: "14px 16px 16px",
                }, children: _jsx(ProfileSectionList, { sections: sections }) }))] }));
}
function buildProfileSections(display, t) {
    const sections = [];
    if (display.intro || display.profileSummary) {
        sections.push({
            key: "about",
            title: t("aboutDoctor", "About the Doctor"),
            body: (_jsxs(_Fragment, { children: [display.intro && _jsx(AboutParagraph, { text: display.intro }), display.profileSummary && (_jsx(AboutParagraph, { text: display.profileSummary, sx: { mt: display.intro ? 1.25 : 0 } }))] })),
        });
    }
    const credentialRows = [
        ...display.qualifications.map((q, i) => ({
            key: `qual-${i}`,
            left: cleanText(getQualificationSecondaryText(q)) ?? "",
            right: q.year ? String(q.year) : undefined,
        })),
        ...cleanList(display.education).map((item, i) => ({
            key: `edu-${i}`,
            left: item,
        })),
        ...cleanList(display.awards).map((item, i) => ({
            key: `award-${i}`,
            left: item,
        })),
    ].filter((row) => row.left.length > 0);
    if (credentialRows.length > 0) {
        sections.push({
            key: "credentials",
            title: t("qualificationAndAwards", "Qualification and Awards"),
            body: _jsx(DetailList, { items: credentialRows }),
        });
    }
    const expertise = cleanList(display.expertise);
    if (expertise.length > 0) {
        sections.push({
            key: "expertise",
            title: t("expertise", "Expertise"),
            body: _jsx(TagChipRow, { items: expertise }),
        });
    }
    const services = cleanList(display.services);
    if (services.length > 0) {
        sections.push({
            key: "services",
            title: t("services", "Services"),
            body: (_jsx(Stack, { spacing: 1, children: services.map((item, i) => (_jsxs(Stack, { direction: "row", alignItems: "flex-start", spacing: 1, children: [_jsx(CheckCircleIcon, { sx: {
                                fontSize: 18,
                                color: "primary.main",
                                mt: "1px",
                                flexShrink: 0,
                            } }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { lineHeight: 1.4 }, children: item })] }, `${item}-${i}`))) })),
        });
    }
    const availableDays = cleanText(display.availableDays);
    if (availableDays) {
        sections.push({
            key: "availability",
            title: t("availability", "Availability"),
            body: (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { lineHeight: 1.5 }, children: availableDays })),
        });
    }
    const languages = cleanList(display.languages);
    if (languages.length > 0) {
        sections.push({
            key: "languages",
            title: t("languages", "Languages"),
            body: _jsx(TagChipRow, { items: languages }),
        });
    }
    const reviewUrl = cleanText(display.googleReviewUrl);
    if (reviewUrl) {
        sections.push({
            key: "reviews",
            title: t("reviews", "Reviews"),
            body: (_jsx(Link, { href: reviewUrl, target: "_blank", rel: "noopener noreferrer", underline: "hover", variant: "body2", sx: { fontWeight: 600 }, children: t("viewGoogleReviews", "View Google reviews") })),
        });
    }
    if (hasDoctorContactInfo(display)) {
        sections.push({
            key: "contact",
            title: t("contact", "Contact"),
            body: _jsx(DoctorContactSection, { display: display }),
        });
    }
    return sections;
}
function ProfileSectionList({ sections, }) {
    return (_jsx(Box, { children: sections.map((section, index) => (_jsxs(Box, { sx: {
                pt: index === 0 ? 0 : 2,
                mt: index === 0 ? 0 : 2,
                borderTop: index === 0 ? 0 : 1,
                borderColor: "divider",
            }, children: [_jsx(InfoSectionHeader, { title: section.title }), section.body] }, section.key))) }));
}
function DoctorListRow({ doctor, display, consultationMode, feeAudience, isSelected, onSelect, }) {
    const { t } = useTranslation("appointment");
    const { displayName, specializationText, experienceText, initials, avatarStyle, profileImageUrl, feeText, } = getIdentityData(doctor, display, consultationMode, feeAudience);
    const rating = display.googleRating;
    return (_jsx(Box, { onClick: onSelect, onKeyDown: (event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect();
            }
        }, role: "button", tabIndex: 0, "aria-pressed": isSelected, sx: {
            p: 1.25,
            borderRadius: 2,
            border: 1,
            borderColor: isSelected ? "primary.main" : "divider",
            bgcolor: isSelected ? "primary.light" : "background.paper",
            cursor: "pointer",
            transition: "all 0.18s",
            boxShadow: isSelected ? "0 0 0 3px rgba(21,59,98,0.10)" : "none",
            "&:hover": {
                borderColor: "primary.main",
                bgcolor: isSelected ? "primary.light" : "action.hover",
            },
            "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 1,
            },
        }, children: _jsxs(Stack, { direction: "row", spacing: 1.25, alignItems: "center", children: [_jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(DoctorAvatar, { displayName: displayName, photoUrl: profileImageUrl, initials: initials, avatarStyle: avatarStyle, size: 56, fontSize: 18 }) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [displayName && (_jsx(Typography, { variant: "body2", sx: {
                                fontWeight: 700,
                                lineHeight: 1.3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }, children: displayName })), specializationText && (_jsx(Typography, { variant: "caption", sx: {
                                color: "primary.main",
                                fontWeight: 500,
                                display: "block",
                                mt: 0.125,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }, children: specializationText })), _jsxs(Stack, { direction: "row", alignItems: "center", flexWrap: "wrap", rowGap: 0.25, columnGap: 0.75, sx: { mt: 0.5 }, children: [rating != null && (_jsxs(_Fragment, { children: [_jsx(StarIcon, { sx: { fontSize: 13, color: "#cba34c" } }), _jsx(Typography, { variant: "caption", sx: { fontWeight: 600, color: "text.primary" }, children: rating.toFixed(1) })] })), experienceText && (_jsxs(_Fragment, { children: [rating != null && _jsx(MetaDot, {}), _jsx(Typography, { variant: "caption", color: "text.secondary", children: t("yearsExp", undefined, { count: experienceText }) })] })), feeText && (_jsxs(_Fragment, { children: [(rating != null || experienceText) && _jsx(MetaDot, {}), _jsx(Typography, { variant: "caption", sx: { fontWeight: 600, color: "text.primary" }, children: feeText })] }))] })] })] }) }));
}
function MetaDot() {
    return (_jsx(Box, { sx: {
            width: 3,
            height: 3,
            borderRadius: "50%",
            bgcolor: "text.disabled",
            flexShrink: 0,
        } }));
}
function FeatureItem({ icon, text, dense = false, }) {
    return (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: dense ? 0.75 : 1.25, sx: dense ? { minWidth: 0 } : undefined, children: [_jsx(Box, { sx: { display: "flex", flexShrink: 0 }, children: icon }), _jsx(Typography, { variant: dense ? "caption" : "body2", sx: { fontWeight: 500, lineHeight: 1.4 }, children: text })] }));
}
function AboutParagraph({ text, sx, }) {
    return (_jsx(Typography, { variant: "body2", color: "text.secondary", component: "div", sx: {
            lineHeight: 1.6,
            "& p": { m: 0 },
            "& p + p": { mt: 1 },
            "& ul, & ol": { pl: 2.5, m: 0 },
            "& a": { color: "primary.main" },
            ...sx,
        }, dangerouslySetInnerHTML: { __html: sanitizeRichHtml(text) } }));
}
function InfoSectionHeader({ title }) {
    return (_jsx(Typography, { sx: {
            fontSize: 15,
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.3,
            mb: 1.25,
        }, children: title }));
}
function TagChipRow({ items }) {
    return (_jsx(Stack, { direction: "row", flexWrap: "wrap", gap: 0.75, children: items.map((item, i) => (_jsx(Box, { sx: {
                border: 1,
                borderColor: "divider",
                borderRadius: 1.25,
                px: 1,
                py: 0.375,
                fontSize: 12,
                lineHeight: 1.4,
                color: "text.secondary",
            }, children: item }, `${item}-${i}`))) }));
}
function DetailList({ items, }) {
    return (_jsx(Box, { children: items.map((item, index) => (_jsxs(Box, { sx: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                pt: index === 0 ? 0 : 0.75,
                pb: index === items.length - 1 ? 0 : 0.75,
                borderBottom: index === items.length - 1 ? 0 : 1,
                borderColor: "divider",
            }, children: [_jsx(Typography, { variant: "body2", sx: { flex: 1, mr: 1.5 }, children: item.left }), item.right && (_jsx(Typography, { variant: "caption", color: "text.disabled", sx: { fontVariantNumeric: "tabular-nums", flexShrink: 0 }, children: item.right }))] }, item.key))) }));
}
function DoctorAvatar({ displayName, photoUrl, initials, avatarStyle, size, fontSize, }) {
    return (_jsx(MuiAvatar, { src: photoUrl || undefined, alt: displayName, sx: {
            width: size,
            height: size,
            fontSize,
            fontWeight: 700,
            letterSpacing: -0.5,
            bgcolor: avatarStyle.bg,
            color: avatarStyle.color,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }, children: initials }));
}
